import { create } from 'zustand'

import { API_CONFIG, API_ENDPOINTS } from '@/config/api'
import { apiClient } from '@/config/apiClient'

export interface BrandGameAvailability {
  brandId: string
  availableGames: number
  nextResetAt: string
}

export interface Brand {
  id: number
  brandId: string
  brandCategoryId: string
  earnedGamesMiniCampaignId: number
  activeFrom: string
  activeUntil: string
  countdownEnabled: boolean
  teaserEnabled: boolean
  gameId: string
  /** URL of the game web client. May be null when no game is configured. */
  gameUrl: string | null
  position: number
  availableGames?: number
  gamesLimit?: number
  nextResetAt?: string
}

interface BrandsResponse {
  active: Brand[]
  upcoming: Brand[]
  expired: Brand[]
}

export type CampaignState =
  | 'active'
  | 'coming_soon'
  | 'maintenance'
  | 'finished'
  | 'unknown'
  | 'error'

interface BrandsState {
  brands: Brand[]
  campaignState: CampaignState
  isLoading: boolean
  error: string | undefined
}

interface BrandsActions {
  fetchBrands: () => Promise<CampaignState>
  fetchAvailableGamesForBrands: () => Promise<void>
  reset: () => void
}

function resolveCampaignState(response: BrandsResponse): CampaignState {
  const now = new Date()
  const active = response.active.filter(b => new Date(b.activeUntil) > now)
  const { upcoming, expired } = response

  if (active.length > 0) return 'active'
  if (upcoming.length > 0 && expired.length === 0) return 'coming_soon'
  if (upcoming.length > 0 && expired.length > 0) return 'maintenance'
  if (expired.length > 0) return 'finished'
  return 'unknown'
}

export const useBrandsStore = create<BrandsState & BrandsActions>(
  (set, get) => ({
    brands: [],
    campaignState: 'unknown',
    isLoading: false,
    error: undefined,

    fetchBrands: async () => {
      set({ isLoading: true, error: undefined })
      try {
        const response = (await apiClient.get(
          API_ENDPOINTS.brands(API_CONFIG.programCode)
        )) as BrandsResponse
        const campaignState = resolveCampaignState(response)
        set({ brands: response.active, campaignState, isLoading: false })
        return campaignState
      } catch {
        set({ isLoading: false, campaignState: 'error' })
        return 'error'
      }
    },

    fetchAvailableGamesForBrands: async () => {
      const availability = (await apiClient.get(
        API_ENDPOINTS.availableGamesForBrands(API_CONFIG.programCode)
      )) as BrandGameAvailability[]

      const availabilityMap = new Map(
        availability.map(item => [item.brandId, item])
      )

      const updatedBrands = get().brands.map(brand => {
        const data = availabilityMap.get(brand.brandId)
        if (!data) return brand
        return {
          ...brand,
          availableGames: data.availableGames,
          nextResetAt: data.nextResetAt,
        }
      })

      set({ brands: updatedBrands })
    },

    reset: () =>
      set({
        brands: [],
        campaignState: 'unknown',
        isLoading: false,
        error: undefined,
      }),
  })
)
